import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { DealDocuments } from "@/components/deals/DealDocuments";
import { ContractVersioning } from "@/components/deals/ContractVersioning";
import { AIDealAnalysis } from "@/components/deals/AIDealAnalysis";
import { DealConversation } from "@/components/deals/DealConversation";
import { RenewalTracker } from "@/components/deals/RenewalTracker";
import { ActivityFeed } from "@/components/deals/ActivityFeed";
import { FulfillmentTracker } from "@/components/deals/FulfillmentTracker";
import { PaymentScheduleTracker } from "@/components/deals/PaymentScheduleTracker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Calendar, DollarSign, FileText, User } from "lucide-react";
import { Loader2 } from "lucide-react";

const DealDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: deal, isLoading } = useQuery({
    queryKey: ["deal", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals")
        .select(`
          *,
          athletes (
            name,
            sport,
            year
          )
        `)
        .eq("id", id)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!deal) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Deal not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold">{deal.title}</h1>
              <p className="text-lg text-muted-foreground mt-1">{deal.brand_name}</p>
            </div>
            <Badge variant={deal.status === "active" ? "default" : "secondary"}>
              {deal.status.replace("_", " ")}
            </Badge>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Deal Amount
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{formatCurrency(deal.amount)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <User className="h-4 w-4" />
                Athlete
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-medium">{deal.athletes?.name}</p>
              <p className="text-sm text-muted-foreground">{deal.athletes?.sport}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Deal Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-medium capitalize">{deal.deal_type.replace("_", " ")}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Duration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{new Date(deal.start_date).toLocaleDateString()}</p>
              <p className="text-sm text-muted-foreground">
                to {deal.end_date ? new Date(deal.end_date).toLocaleDateString() : "Ongoing"}
              </p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="fulfillment">Fulfillment</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="conversation">Conversation</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="contract-history">Contract History</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <AIDealAnalysis deal={deal} />
              <RenewalTracker dealId={deal.id} endDate={deal.end_date} />
            </div>
          </TabsContent>

          <TabsContent value="fulfillment">
            <FulfillmentTracker dealId={deal.id} />
          </TabsContent>

          <TabsContent value="payments">
            <PaymentScheduleTracker dealId={deal.id} totalAmount={deal.amount} />
          </TabsContent>

          <TabsContent value="conversation">
            <DealConversation dealId={id!} />
          </TabsContent>

          <TabsContent value="activity">
            <ActivityFeed dealId={id!} />
          </TabsContent>

          <TabsContent value="documents">
            <DealDocuments dealId={deal.id} />
          </TabsContent>

          <TabsContent value="contract-history">
            <ContractVersioning dealId={deal.id} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default DealDetail;
